import React, { useMemo, useCallback } from 'react';
import { Label } from 'src/components/ui/label';
import SelectDropdown, {
  SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import {
  AI_MODELS,
  AI_MODEL_OPTIONS,
  SupportedModelId,
  AiModelOption,
} from 'src/types/aiModels';

export interface AIModelSelectorProps {
  /** Currently selected model */
  selectedModel: SupportedModelId;
  /** Callback when model changes */
  onModelChange: (model: SupportedModelId) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Optional label for the selector */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: 'small' | 'default';
  /** Whether to take full width */
  fullWidth?: boolean;
  /** Custom class name */
  className?: string;
  /**
   * Custom model order - if not provided, uses default priority:
   * Claude Opus 4.1 -> Claude 4 Sonnet -> GPT-4o -> GPT-5
   */
  customOrder?: SupportedModelId[];
  /** Portal container for dropdowns in popup windows */
  portalContainer?: HTMLElement | null;
}

/**
 * Shared AI Model Selector Component
 *
 * Provides consistent model selection across the application with:
 * - Intelligent model ordering (premium models first)
 * - Type-safe model handling
 * - Consistent UI/UX
 * - Portal support for popup windows
 * - Customizable ordering when needed
 */
export function AIModelSelector({
  selectedModel,
  onModelChange,
  disabled = false,
  label,
  placeholder = 'Select model...',
  size = 'default',
  fullWidth = true,
  className,
  customOrder,
  portalContainer: _portalContainer,
}: AIModelSelectorProps) {
  // Default intelligent ordering: Premium models first, then standard
  const defaultModelOrder: SupportedModelId[] =
    process.env.NODE_ENV === 'production'
      ? [
          AI_MODELS.CLAUDE_OPUS_4_1,
          AI_MODELS.CLAUDE_4_SONNET,
          AI_MODELS.GPT_4O,
          AI_MODELS.GPT_5,
        ]
      : [
          AI_MODELS.GPT_4O,
          AI_MODELS.CLAUDE_OPUS_4_1,
          AI_MODELS.CLAUDE_4_SONNET,
          AI_MODELS.GPT_5,
        ];

  const modelOrder = customOrder || defaultModelOrder;

  // Create ordered model options based on priority
  const orderedModelOptions = useMemo(() => {
    const orderedOptions: AiModelOption[] = [];

    // Add models in the specified order
    for (const modelId of modelOrder) {
      const option = AI_MODEL_OPTIONS.find((o) => o.id === modelId);
      if (option) {
        orderedOptions.push(option);
      }
    }

    // Add any remaining models that weren't in the order list
    for (const option of AI_MODEL_OPTIONS) {
      if (!orderedOptions.find((o) => o.id === option.id)) {
        orderedOptions.push(option);
      }
    }

    return orderedOptions.filter((option): option is AiModelOption =>
      Boolean(option),
    );
  }, [modelOrder]);

  const selectedModelOption = useMemo(
    () => orderedModelOptions.find((o) => o.value === selectedModel) || null,
    [orderedModelOptions, selectedModel],
  );

  const handleModelChange = useCallback(
    (option: SelectDropdownOption | null) => {
      if (option) {
        onModelChange(option.value as SupportedModelId);
      }
    },
    [onModelChange],
  );

  // If we need portal support (for popup windows), we could extend this
  // For now, use the standard SelectDropdown
  const dropdown = (
    <SelectDropdown
      options={orderedModelOptions}
      value={selectedModelOption}
      onChange={handleModelChange}
      placeholder={placeholder}
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      className={className}
    />
  );

  if (label) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">{label}</Label>
        {dropdown}
      </div>
    );
  }

  return dropdown;
}

/**
 * Hook to get alternative model suggestions for error scenarios
 */
export function useModelFallback() {
  const getAlternativeModel = useCallback(
    (currentModel: SupportedModelId): SupportedModelId | null => {
      // Use the same default order as the selector
      const defaultOrder: SupportedModelId[] = [
        AI_MODELS.CLAUDE_OPUS_4_1,
        AI_MODELS.CLAUDE_4_SONNET,
        AI_MODELS.GPT_4O,
        AI_MODELS.GPT_5,
      ];

      const currentIndex = defaultOrder.indexOf(currentModel);

      // If current model not found in order, return first model
      if (currentIndex === -1) {
        return defaultOrder[0];
      }

      // Return next model in order, or null if already at the last one
      if (currentIndex < defaultOrder.length - 1) {
        return defaultOrder[currentIndex + 1];
      }

      // Already at the last model, no alternatives
      return null;
    },
    [],
  );

  const getModelDisplayName = useCallback(
    (modelId: SupportedModelId): string => {
      const option = AI_MODEL_OPTIONS.find((o) => o.id === modelId);
      return option?.label || modelId;
    },
    [],
  );

  return {
    getAlternativeModel,
    getModelDisplayName,
  };
}

export default AIModelSelector;
